/**
 * @fileoverview A component that provides pagination functionality for a list of items.
 * It includes a previous and next button, and displays the current page and total number of pages.
 *
 * @example
 * <Pagination total={100} />
 */

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import { Link } from 'react-router-dom'

/**
 * @interface PaginationProps
 * @description Props for the Pagination component.
 * @property {number} total - The total number of items.
 */
interface PaginationProps {
    total: number
}

/**
 * @function Pagination
 * @description Component for providing pagination functionality.
 * @param {PaginationProps} props - The props for the Pagination component.
 * @returns {JSX.Element | null} - The Pagination component.
 */
export function Pagination({ total }: PaginationProps) {
    const searchParams = new URLSearchParams(location.search)
    const offset = parseInt(searchParams.get('offset')) || 0
    const limit = parseInt(searchParams.get('limit')) || 50
    const prevOffset = Math.max(offset - limit, 0)

    let nextOffset: number

    if (offset + limit >= total || offset >= total || total - (offset + limit) < limit) {
        nextOffset = Math.max(total - limit, 0)
    } else {
        nextOffset = offset + limit
    }

    searchParams.set('limit', limit.toString())
    searchParams.set('offset', prevOffset.toString())
    const prevLink = searchParams.toString()
    searchParams.set('offset', nextOffset.toString())
    const nextLink = searchParams.toString()

    const displayFrom = offset + 1
    const displayTo = Math.min(offset + limit, total)

    if (total <= limit) return null

    return (
        <>
            <div className="flex ml-8 mr-4">
                <Link
                    to={{ search: prevLink }}
                    className="flex items-center rounded-md border border-gray-300 bg-white w-full mr-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                    <ChevronLeftIcon className="w-5 m-2.5 h-5 text-gray-400" />
                </Link>
                <Link
                    to={{ search: nextLink }}
                    className="flex items-center rounded-md border border-gray-300 bg-white w-full text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                    <ChevronRightIcon className="w-5 m-2.5 h-5 text-gray-400" />
                </Link>
            </div>
            <div className="flex">
                <span className="font-medium text-xs text-gray-900">
                    {displayFrom} - {displayTo}
                    <span className="font-regular text-gray-500"> of {total}</span>
                </span>
            </div>
        </>
    )
}
